import { ArrowLeft, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { ArticleVisuals } from "../components/ArticleVisuals";
import { MarkdownBody } from "../components/MarkdownBody";
import { TopographicLines } from "../components/TopographicLines";
import type { BlogPost } from "../data/content";
import { fallbackPostsForLocale } from "../data/content";
import { useI18n, type Locale } from "../i18n";
import { ApiRequestError, getPost } from "../lib/api";
import { formatPublicationDate } from "../lib/date";
import { setPageMetadata } from "../lib/metadata";

export function ArticlePage() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const { locale, messages } = useI18n();
  const copy = messages.blog;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const fallback = fallbackPostsForLocale(locale).find((post) => post.slug === slug);
  const [postState, setPostState] = useState<
    { locale: Locale; post: BlogPost } | undefined
  >();
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const remotePost =
    postState?.locale === locale && postState.post?.slug === slug
      ? postState.post
      : undefined;
  const visiblePost = remotePost;

  useEffect(() => {
    const controller = new AbortController();

    setPostState(undefined);
    setNotFound(false);
    setLoadFailed(false);

    void getPost(slug, locale, controller.signal)
      .then((nextPost) => {
        setPostState({ locale, post: nextPost });
        setNotFound(false);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        if (error instanceof ApiRequestError && error.status === 404) {
          setPostState(undefined);
          setNotFound(true);
          return;
        }

        const canUseFallback =
          fallback !== undefined &&
          (!(error instanceof ApiRequestError) || error.status >= 500);
        if (canUseFallback) {
          setPostState({ locale, post: fallback });
        } else {
          setLoadFailed(true);
        }
      });

    return () => controller.abort();
  }, [slug, locale, fallback]);

  useEffect(() => {
    let frame = 0;
    if (visiblePost) {
      setPageMetadata(
        `${visiblePost.title} — SOURCE / SAND`,
        visiblePost.dek
      );
    } else if (notFound) {
      setPageMetadata(
        `${copy.article.notFoundTitle} — SOURCE / SAND`,
        copy.article.notFoundBody
      );
    } else if (loadFailed) {
      setPageMetadata(
        `${copy.article.outageTitle} — SOURCE / SAND`,
        copy.article.outageBody
      );
    }

    if ((visiblePost || notFound || loadFailed) && location.key !== "default") {
      frame = requestAnimationFrame(() => {
        titleRef.current?.setAttribute("data-route-focus", "true");
        titleRef.current?.focus({ preventScroll: true });
      });
    }
    return () => cancelAnimationFrame(frame);
  }, [copy, loadFailed, location.key, notFound, visiblePost]);

  if (!visiblePost && notFound) {
    return (
      <>
        <Header />
        <main className="not-found shell">
          <p className="eyebrow">{copy.article.notFoundEyebrow}</p>
          <h1 ref={titleRef} tabIndex={-1}>
            {copy.article.notFoundTitle}
          </h1>
          <p>{copy.article.notFoundBody}</p>
          <Link className="button" to="/journal">
            {copy.article.backToJournal}
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  if (!visiblePost && loadFailed) {
    return (
      <>
        <Header />
        <main className="not-found shell">
          <p className="eyebrow">{copy.article.outageEyebrow}</p>
          <h1 ref={titleRef} tabIndex={-1}>
            {copy.article.outageTitle}
          </h1>
          <p>{copy.article.outageBody}</p>
          <button className="button" type="button" onClick={() => window.location.reload()}>
            {copy.article.tryAgain}
          </button>
          <Link className="text-link" to="/journal">
            {copy.article.backToJournal}
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  if (!visiblePost) {
    return (
      <>
        <Header />
        <main
          className="not-found shell"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <h1 ref={titleRef} tabIndex={-1}>
            {copy.article.loading}
          </h1>
        </main>
        <Footer />
      </>
    );
  }

  const publicationDate = formatPublicationDate(
    visiblePost.publishedAt,
    "long",
    locale
  );

  return (
    <>
      <Header />
      <main className="article-page">
        <header className="article-hero">
          <TopographicLines className="article-hero__contours" />
          <div className="shell article-hero__inner">
            <Link className="back-link" to="/journal">
              <ArrowLeft size={17} aria-hidden="true" />
              {copy.article.allNotes}
            </Link>
            <p className="story-card__meta">
              {copy.categories[visiblePost.category] ?? visiblePost.category} ·{" "}
              {copy.common.minutes(visiblePost.readingMinutes)} ·{" "}
              {publicationDate}
            </p>
            <h1 ref={titleRef} tabIndex={-1}>
              {visiblePost.title}
            </h1>
            <p className="article-hero__dek">{visiblePost.dek}</p>
          </div>
          <div className="article-hero__media">
            <img
              src={visiblePost.heroImage.src}
              alt={visiblePost.heroImage.alt}
              width="1536"
              height="1024"
              fetchPriority="high"
            />
          </div>
        </header>
        <article className="article-content shell">
          <div className="article-story">
            <ArticleVisuals
              visuals={visiblePost.visuals}
              label={copy.article.visualsLabel}
            />
            <MarkdownBody markdown={visiblePost.bodyMarkdown} />
          </div>
          <section
            className="article-sources"
            aria-labelledby="article-sources-title"
          >
            <h2 id="article-sources-title">{copy.article.sourcesTitle}</h2>
            <p>
              {copy.article.sourcesIntro}{" "}
              {publicationDate}.
            </p>
            <ul>
              {visiblePost.sources.map((source) => (
                <li key={source.url}>
                  <a href={source.url} target="_blank" rel="noreferrer">
                    {source.label}
                    <ExternalLink size={15} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}
